import axios from "./axios";

export async function checkCustomerNumber(payload) {
  return axios.get(`/customers/checkNumber`, {
    params: payload,
  });
}

export async function checkCustomerEmail(payload) {
  return axios.get(`/customers/checkEmail`, {
    params: payload,
  });
}

export async function getCustomerAgreements(customerId) {
  return axios
    .get(`/customers/${customerId}/agreements`)
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      return Promise.reject(error.response?.data || error);
    });
}

export async function fetchCustomers(controller) {
  return axios({ method: "get", url: "/customers", params: controller })
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      return Promise.reject(error.response?.data || error);
    });
}

export async function fetchCustomer(id) {
  return axios.get(`/customers/` + id);
}

export async function lockCustomer(id, { reason } = {}) {
  const response = await axios.post(`/customers/${id}/lock`, { reason });
  if (response.status >= 400) {
    return Promise.reject(response.data ?? { message: response.statusText });
  }
  return response.data;
}

export async function unlockCustomer(id, { note } = {}) {
  const response = await axios.post(`/customers/${id}/unlock`, { note });
  if (response.status >= 400) {
    return Promise.reject(response.data ?? { message: response.statusText });
  }
  return response.data;
}

export async function retryCustomerTripletexSync(id) {
  return axios
    .post(`/customers/${id}/retry_tripletex_sync`)
    .then((response) => {
      return response.data;
    });
}

export async function createCustomer(payload) {
  const response = await axios.post(`/customers/`, { customer: payload });
  // Global axios interceptor resolves error.response — treat non-OK as failure
  if (response.status >= 400) {
    return Promise.reject(response.data ?? { message: response.statusText });
  }
  return response.data;
}

export async function updateCustomer(payload) {
  const response = await axios.put(`/customers/${payload.id}`, {
    customer: payload,
  });
  if (response.status >= 400) {
    return Promise.reject(response.data ?? { message: response.statusText });
  }
  return response.data;
}

export async function destroyCustomer(payload) {
  return axios
    .delete(`/customers/${payload.id}`, {
      data: JSON.stringify({
        payload,
      }),
    })
    .then((response) => {
      return response;
    })
    .catch((error) => {
      return Promise.reject(error.response?.data || error);
    });
}

export async function fetchCustomerContacts(controller) {
  return axios({ method: "get", url: "/contacts/", params: controller })
    .then((response) => response.data)
    .catch((error) => Promise.reject(error.response?.data || error));
}

export async function fetchCustomerContact(id) {
  return axios.get(`/contacts/` + id);
}

export async function createCustomerContact(payload) {
  return axios
    .post(`/contacts`, { contact: payload })
    .then((response) => response.data)
    .catch((error) => Promise.reject(error.response?.data || error));
}

export async function createCustomerCommunication(customerId, payload) {
  return axios
    .post(`/customers/${customerId}/communications`, { communication: payload })
    .then((response) => response.data)
    .catch((error) => Promise.reject(error.response?.data || error));
}

export async function fetchCustomerCommunications(customerId, controller = {}) {
  return axios({
    method: "get",
    url: `/customers/${customerId}/communications`,
    params: controller,
  })
    .then((response) => response.data)
    .catch((error) => Promise.reject(error.response?.data || error));
}

export async function updateCustomerCommunication(customerId, communicationId, payload) {
  return axios
    .put(`/customers/${customerId}/communications/${communicationId}`, { communication: payload })
    .then((response) => response.data)
    .catch((error) => Promise.reject(error.response?.data || error));
}

export async function fetchCustomerEvents(customerId, controller = {}) {
  return axios({
    method: "get",
    url: `/customers/${customerId}/events`,
    params: controller,
  })
    .then((response) => response.data)
    .catch((error) => Promise.reject(error.response?.data || error));
}

export async function fetchAllCommunications(controller = {}) {
  return axios({
    method: "get",
    url: "/communications",
    params: controller,
  })
    .then((response) => response.data)
    .catch((error) => Promise.reject(error.response?.data || error));
}

/**
 * Compact previous works for a customer (AI lines, excerpts, checklist highlights, images).
 * GET /customers/:customerId/previous_works
 *
 * Optional params: limit, skip_ai, exclude_report_id,
 * customer_system_id / system_id (filter to facility),
 * reference_system_id (mark same/different facility vs current report).
 */
export async function fetchCustomerPreviousWorks(customerId, params = {}) {
  if (!customerId) return { data: [], count: 0, ai_overview: null };
  return axios({
    method: "get",
    url: `/customers/${customerId}/previous_works`,
    params,
  })
    .then((response) => response.data)
    .catch((error) => {
      return Promise.reject(error.response?.data || error);
    });
}